import { useEffect, useState } from "react";
import { http, formatErr } from "../../lib/api";
import { downloadB64 } from "../../lib/files";
import { usePolling } from "../../lib/usePolling";
import { ClientSelect } from "../../components/ClientSelect";
import { toast } from "sonner";
import { Plus, Download, Trash2 } from "lucide-react";

export default function AgentOfferte() {
  const [list, setList] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ contact_id: null, customer_name: "", salon_name: "", notes: "" });
  const [items, setItems] = useState([{ label: "", price_ref: "", price_proposed: "" }]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await http.get("/crm/me/offers"); setList(data); setBlocked(false); }
    catch (e) { if (e?.response?.status === 403) { setBlocked(true); setList([]); } else toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  usePolling(load, { interval: 10000, enabled: !blocked });

  const addItem = () => setItems((s) => [...s, { label: "", price_ref: "", price_proposed: "" }]);
  const rmItem = (i) => setItems((s) => s.filter((_, idx) => idx !== i));
  const setItem = (i, k, v) => setItems((s) => s.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const create = async (e) => {
    e.preventDefault();
    const payloadItems = items.filter((it) => it.label.trim()).map((it) => ({
      label: it.label, price_ref: it.price_ref === "" ? null : parseFloat(it.price_ref),
      price_proposed: parseFloat(it.price_proposed || 0),
    }));
    if (payloadItems.length === 0) return toast.error("Aggiungi almeno una voce con etichetta.");
    setBusy(true);
    try {
      await http.post("/crm/me/offers", { ...form, items: payloadItems });
      setShow(false); setForm({ contact_id: null, customer_name: "", salon_name: "", notes: "" }); setItems([{ label: "", price_ref: "", price_proposed: "" }]);
      await load(); toast.success("Offerta generata (PDF pronto).");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const dl = async (id) => {
    try { const { data } = await http.get(`/crm/me/offers/${id}/download`); downloadB64(data.data_b64, data.filename, data.content_type); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const setStatus = async (id, status) => {
    try { await http.put(`/crm/me/offers/${id}/status`, null, { params: { status } }); await load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (blocked) return <div data-testid="agent-offers-blocked" className="max-w-2xl mx-auto card-clean p-6 text-center"><h1 className="font-serif text-2xl mb-2">Offerte</h1><p className="text-sm text-neutral-600">Disponibili dopo l'attivazione del tuo account.</p></div>;
  if (list === null) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

  return (
    <div data-testid="agent-offers-page" className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-serif text-3xl">Offerte / Preventivi</h1>
        <button onClick={() => setShow((v) => !v)} data-testid="agent-offer-new" className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-2" style={{ background: "var(--brand-dark)" }}><Plus className="w-4 h-4" /> Nuova offerta</button>
      </div>
      {show && (
        <form onSubmit={create} className="card-clean p-5 mb-5 space-y-3 text-sm" data-testid="agent-offer-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Cliente registrato</span><ClientSelect testid="ao-client" value={{ contact_id: form.contact_id, salon_name: form.salon_name }} onChange={(v) => setForm({ ...form, contact_id: v.contact_id, salon_name: v.salon_name, customer_name: v.salon_name })} /></div>
            <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Intestatario (se diverso)</span><input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="ao-customer" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} /></label>
          </div>
          <div className="space-y-2" data-testid="ao-items">
            {items.map((it, i) => (
              <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2 items-center">
                <input placeholder="Voce" value={it.label} onChange={(e) => setItem(i, "label", e.target.value)} data-testid={`ao-item-label-${i}`} className="w-full sm:w-auto sm:flex-1 min-w-0 px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
                <input placeholder="Rif. €" type="number" step="0.01" value={it.price_ref} onChange={(e) => setItem(i, "price_ref", e.target.value)} data-testid={`ao-item-ref-${i}`} className="flex-1 min-w-0 sm:flex-none sm:w-28 px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
                <input placeholder="Proposto €" type="number" step="0.01" value={it.price_proposed} onChange={(e) => setItem(i, "price_proposed", e.target.value)} data-testid={`ao-item-prop-${i}`} className="flex-1 min-w-0 sm:flex-none sm:w-28 px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
                {items.length > 1 && <button type="button" onClick={() => rmItem(i)} className="p-2 text-rose-600"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
            <button type="button" onClick={addItem} data-testid="ao-add-item" className="text-xs px-3 py-1.5 rounded-md border" style={{ borderColor: "var(--border-silver)" }}>+ Voce</button>
          </div>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Note</span><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ao-notes" className="w-full px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} /></label>
          <button type="submit" disabled={busy} data-testid="agent-offer-save" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>Genera offerta PDF</button>
        </form>
      )}
      <div className="card-clean overflow-x-auto" data-testid="agent-offers-list">
        {list.length === 0 ? <div className="p-6 text-sm text-neutral-500">Nessuna offerta.</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b" style={{ borderColor: "var(--border-silver)" }}><th className="px-4 py-3">N.</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {list.map((o) => (
                <tr key={o.id} className="border-b last:border-0" style={{ borderColor: "var(--border-silver)" }} data-testid={`agent-offer-row-${o.id}`}>
                  <td className="px-4 py-3 font-medium">{o.number}</td>
                  <td className="px-4 py-3">{o.customer_name || o.salon_name || "—"}</td>
                  <td className="px-4 py-3"><select value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} data-testid={`agent-offer-status-${o.id}`} className="px-2 py-1 rounded border text-xs" style={{ borderColor: "var(--border-silver)" }}>{["bozza", "inviata", "accettata", "rifiutata"].map((s) => <option key={s} value={s}>{s}</option>)}</select></td>
                  <td className="px-4 py-3"><button onClick={() => dl(o.id)} data-testid={`agent-offer-dl-${o.id}`} className="px-2 py-1 rounded border text-xs flex items-center gap-1" style={{ borderColor: "var(--border-silver)" }}><Download className="w-3 h-3" /> PDF</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
